import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Bell, Globe, Moon, Sun, Volume2, Info, LogOut, Eye, MessageSquare, MapPin, Palette, Gavel, Ban, UserX, ChevronDown, ChevronUp, Trash2, ShieldCheck, KeyRound, Mail, Phone, Activity, Compass } from 'lucide-react';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useTheme } from './ThemeProvider';
import { useSettings } from './SettingsContext';
import { useUserData } from './UserDataContext';
import { useGuide } from '../guide/useGuide';
import { ParallaxBackground } from './utils/parallax';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { SecuritySetupModal } from './SecuritySetupModal';
import { ChangePasswordModal } from './ChangePasswordModal';
import { VerificationScreen } from './VerificationScreen';
import { useMyActivityVisibility } from '../hooks/useActivityStatus';
import { PresenceService } from '../services/PresenceService';

interface AppSettingsScreenProps {
  onBack: () => void;
}

export function AppSettingsScreen({ onBack }: AppSettingsScreenProps) {
  const { theme, toggleTheme } = useTheme();
  const {
    patternIntensity,
    setPatternIntensity,
    profileVisibility,
    setProfileVisibility,
    allowMessages,
    setAllowMessages,
    shareLocation,
    setShareLocation: setLocalShareLocation,
    notificationPrefs,
    setNotificationPrefs,
  } = useSettings();
  const { updateProfile, userProfile, blockedUsers, unblockUser, firebaseUser } = useUserData();
  const { start: startGuide } = useGuide();
  const showActivityStatus = useMyActivityVisibility();

  const [showBlockedList, setShowBlockedList] = useState(false);
  const [blockedUserProfiles, setBlockedUserProfiles] = useState<Record<string, { name: string; username: string; avatar: string }>>({});
  const [language, setLanguage] = useState('english');
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState<false | 'email' | 'phone'>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [settings, setSettings] = useState({
    notifications: userProfile?.notificationPreferences?.socialNotifications !== false,
    soundEffects: true,
  });

  // Re-authentication state for sensitive operations (Account Deletion)
  const [showReauthDialog, setShowReauthDialog] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [isReauthenticating, setIsReauthenticating] = useState(false);

  // Load minimal profile data for blocked users when panel is opened
  useEffect(() => {
    if (!showBlockedList || blockedUsers.length === 0) return;
    const loadProfiles = async () => {
      const { UserService } = await import('../services/api');
      const missing = blockedUsers.filter(id => !blockedUserProfiles[id]);
      if (missing.length === 0) return;
      
      try {
        const results = await Promise.all(
          missing.map(id => UserService.fetchUserById(id).then(u => ({ id, u })))
        );
        setBlockedUserProfiles(prev => {
          const next = { ...prev };
          results.forEach(({ id, u }) => {
            if (u) next[id] = { name: u.name, username: String(u.username), avatar: u.croppedAvatar || u.avatar || u.image || '' };
          });
          return next;
        });
      } catch (err) {
        console.error('Failed to load blocked profiles:', err);
      }
    };
    loadProfiles();
  }, [showBlockedList, blockedUsers, blockedUserProfiles]);

  /**
   * Run the guide again on demand.
   *
   * Clears the two completion fields as well as starting it, because they are
   * what suppress the automatic run: without clearing them a replay would work
   * once and the state would still say "already seen". This is also the only way
   * an existing account reaches the guide at all, since the automatic path is
   * gated on having just finished onboarding.
   *
   * Leaves the settings screen first. The tour drives navigation itself, and its
   * first step is on the YapTab, so staying here would just be a dim overlay
   * over Settings while it navigates away underneath.
   */
  const handleReplayGuide = async () => {
    try {
      if (firebaseUser?.uid) {
        const { getDbSafe } = await import('../services/firebaseConfig');
        const { doc, updateDoc, deleteField } = await import('firebase/firestore');
        const db = await getDbSafe();
        await updateDoc(doc(db, 'users', firebaseUser.uid), {
          guideCompletedAt: deleteField(),
          guideSkippedAt: deleteField(),
          // A replay is not a skip, so it must not leave a reminder queued.
          guideReminderDueAt: deleteField(),
        });
      }
    } catch (e) {
      // Starting the tour matters more than recording that it was restarted.
      console.warn('[Settings] Could not clear guide state:', e);
    }
    onBack();
    startGuide();
  };

  // 🔒 ONBOARDING FLOW: Auto-open security setup if the user tapped "Set Up Now"
  useEffect(() => {
    const checkPending = async () => {
      try {
        const { SafeStorage } = await import('../utils/safeStorage');
        const pending = SafeStorage.getItem('pending_security_setup');
        if (pending === 'true') {
          SafeStorage.removeItem('pending_security_setup');
          // Short delay so the settings screen finishes mounting
          setTimeout(() => setShowSecurityModal(true), 350);
        }
      } catch (e) {}
    };
    checkPending();
  }, []);

  const handleShareLocationChange = async (enabled: boolean) => {
    setLocalShareLocation(enabled);
    try {
      await updateProfile({ 
        showLocation: enabled,
        showLocationSetAt: new Date().toISOString(),
      } as any);
    } catch (error) {
      toast.error('Failed to save location preference');
    }
  };

  const handleSocialNotificationsChange = async (enabled: boolean) => {
    setSettings(prev => ({ ...prev, notifications: enabled }));
    try {
      await updateProfile({ 
        notificationPreferences: {
          ...(userProfile?.notificationPreferences || {}),
          socialNotifications: enabled
        } 
      });
    } catch (error) {
      toast.error('Failed to save notification preference');
    }
  };

  const toggleSetting = (key: keyof typeof settings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Firebase wants a recent sign-in before deletion. How we obtain it depends on
  // how the account signs in: a password account is asked for its password, a
  // Google account re-consents through Google. Asking a Google account for a
  // password is a dead end — there isn't one — which is what made deletion
  // impossible for those users.
  const handleRecentLoginRequired = async () => {
    const { AuthService } = await import('../services/api');
    if (!(await AuthService.isPasswordlessAccount())) {
      setShowReauthDialog(true);
      return;
    }
    try {
      await AuthService.reauthenticateWithGoogle();
      await executeDeleteAccount();
    } catch (reauthErr: any) {
      const msg = String(reauthErr?.message || '');
      // Closing the Google sheet is a decision, not a failure.
      if (/popup-closed|cancel|user_cancel|closed by user/i.test(msg)) return;
      toast.error('Could not verify with Google. Please try again.');
    }
  };

  const executeDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { UserService } = await import('../services/api');
      await toast.promise(UserService.deleteAccount(), {
        loading: 'Deleting your account and purging data...',
        success: () => {
           window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'login' }));
           return 'Account permanently deleted';
        },
        error: (err: any) => {
          setIsDeleting(false);
          if (err.code === 'auth/requires-recent-login' || err.message?.includes('requires-recent-login')) {
            void handleRecentLoginRequired();
            return 'Security verification required';
          }
          return `Deletion failed: ${err.message}`;
        }
      });
    } catch (err: any) {
      setIsDeleting(false);
      if (err.code === 'auth/requires-recent-login' || err.message?.includes('requires-recent-login')) {
        void handleRecentLoginRequired();
      } else {
        toast.error(err.message || 'Failed to delete account');
      }
    }
  };

  const handleReauthenticateAndDelete = async () => {
    if (!reauthPassword) {
      toast.error('Please enter your password');
      return;
    }

    setIsReauthenticating(true);
    try {
      const { AuthService } = await import('../services/api');
      await AuthService.reauthenticate(reauthPassword);
      setShowReauthDialog(false);
      setReauthPassword('');
      // After successful re-auth, retry deletion
      await executeDeleteAccount();
    } catch (err: any) {
      toast.error(err.message || 'Authentication failed. Please check your password.');
    } finally {
      setIsReauthenticating(false);
    }
  };

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] transition-colors duration-300 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <ScrollArea className="h-full">
        <div className="w-full max-w-2xl mx-auto relative z-10">
          {/* Header */}
          <div
            className="p-4 border-b border-black/10 dark:border-white/10 bg-[#FAF8F5]/80 dark:bg-black/30 backdrop-blur-lg"
            style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 32px))' }}
          >
            <div className="flex items-center gap-4">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                className="text-[#2C3E50] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
              >
                <ArrowLeft className="w-6 h-6" />
              </motion.button>
              <div>
                <h1 className="text-3xl text-black dark:text-white">
                  App Settings
                </h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-sm text-[#373737] dark:text-[#E5E5E5]">Customize your experience</p>

                </div>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* General Settings */}
            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">
                General
              </h2>
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow group">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      <Bell className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                      <div>
                        <h3 className="text-black dark:text-white mb-1">Push Notifications</h3>
                        <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Get notified about connections and messages</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.notifications}
                      onCheckedChange={handleSocialNotificationsChange}
                    />
                  </div>

                  {/* Granular Toggles */}
                  <AnimatePresence>
                    {settings.notifications && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden space-y-3 pt-3 border-t border-black/5 dark:border-white/5"
                      >
                        <p className="text-[10px] font-bold text-[#999999] uppercase tracking-widest mb-2">Individual Toggles</p>
                        
                        {[
                          { id: 'likes', label: 'Likes & Reactions' },
                          { id: 'comments', label: 'Comments & Replies' },
                          { id: 'shouts', label: 'Shouts (Reposts)' },
                          { id: 'messages', label: 'Direct Messages' },
                          { id: 'connections', label: 'New Connections' },
                          { id: 'tribeActivity', label: 'Tribe Activity' },
                          { id: 'yapDay', label: 'YapDay Reset' },
                          { id: 'dailyPrompt', label: 'Daily Yap (Pinned by Legion)' },
                          { id: 'system', label: 'App Alerts' }
                        ].map((notif) => (
                          <div key={notif.id} className="flex items-center justify-between py-1">
                            <span className="text-sm text-[#373737] dark:text-[#e0e0e0] font-medium">{notif.label}</span>
                            <Switch
                              className="scale-90"
                              checked={(notificationPrefs as any)[notif.id] !== false}
                              onCheckedChange={async (checked) => {
                                setNotificationPrefs({ [notif.id]: checked });
                                try {
                                  await updateProfile({
                                    notificationPreferences: {
                                      ...(userProfile?.notificationPreferences || {}),
                                      [notif.id]: checked
                                    }
                                  } as any);
                                } catch (err) {
                                  console.error('Failed to update notif pref:', err);
                                }
                              }}
                            />
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <motion.div
                  className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <motion.div
                        initial={false}
                        animate={{ rotate: theme === 'dark' ? 0 : 180 }}
                        transition={{ duration: 0.3 }}
                      >
                        {theme === 'dark' ? (
                          <Moon className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                        ) : (
                          <Sun className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                        )}
                      </motion.div>
                      <div>
                        <h3 className="text-black dark:text-white mb-1">Dark / Light Mode</h3>
                        <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                          {theme === 'dark' ? 'Dark theme enabled' : 'Light theme enabled'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={theme === 'dark'}
                      onCheckedChange={toggleTheme}
                    />
                  </div>
                </motion.div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Volume2 className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                      <div>
                        <h3 className="text-black dark:text-white mb-1">Sound Effects</h3>
                        <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Play sounds for interactions</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.soundEffects}
                      onCheckedChange={() => toggleSetting('soundEffects')}
                    />
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-2">Language</h3>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="w-full bg-[#FAF8F5] dark:bg-black/30 border-black/20 dark:border-white/20 text-black dark:text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/20 dark:border-white/20 modal-shadow">
                          <SelectItem value="english" className="text-black dark:text-white">English</SelectItem>
                          <SelectItem value="french" className="text-black dark:text-white">Français</SelectItem>
                          <SelectItem value="swahili" className="text-black dark:text-white">Kiswahili</SelectItem>
                          <SelectItem value="yoruba" className="text-black dark:text-white">Yorùbá</SelectItem>
                          <SelectItem value="zulu" className="text-black dark:text-white">isiZulu</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Appearance Settings */}
            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">
                Appearance
              </h2>
              <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <Palette className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  <div className="flex-1">
                    <h3 className="text-black dark:text-white mb-1">Cultural Patterns</h3>
                    <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                      Adjust the intensity of Kente and Adinkra-inspired background patterns
                    </p>
                  </div>
                </div>
                <Select value={patternIntensity} onValueChange={(value: string) => setPatternIntensity(value as any)}>
                  <SelectTrigger className="w-full bg-[#FAF8F5] dark:bg-black/30 border-black/20 dark:border-white/20 text-black dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/20 dark:border-white/20 modal-shadow">
                    <SelectItem value="off" className="text-black dark:text-white">Off</SelectItem>
                    <SelectItem value="subtle" className="text-black dark:text-white">Subtle</SelectItem>
                    <SelectItem value="full" className="text-black dark:text-white">Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </section>

            {/* Help */}
            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">Help</h2>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleReplayGuide}
                className="w-full p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 flex items-center justify-between card-shadow transition-colors hover:bg-[#ecd4a3] dark:hover:bg-[#222222]"
              >
                <div className="flex items-center gap-3">
                  <Compass className="w-5 h-5 text-[#4478ea]" />
                  <div className="text-left">
                    <h3 className="text-black dark:text-white font-medium">Replay app guide</h3>
                    <p className="text-sm text-[#666666] dark:text-[#9a9a9a] mt-0.5">Walk through the app again, one screen at a time</p>
                  </div>
                </div>
                <span className="text-[#4478ea] text-lg font-light">→</span>
              </motion.button>
            </section>

            {/* Security & Account */}
            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">Security &amp; Account</h2>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowSecurityModal(true)}
                className="w-full p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 flex items-center justify-between card-shadow transition-colors hover:bg-[#ecd4a3] dark:hover:bg-[#222222]"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-[#4478ea]" />
                  <div className="text-left">
                    <h3 className="text-black dark:text-white font-medium">Recovery Questions</h3>
                    <p className="text-sm text-[#666666] dark:text-[#9a9a9a] mt-0.5">Set a PIN and security questions to recover your account</p>
                  </div>
                </div>
                <span className="text-[#4478ea] text-lg font-light">→</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowChangePasswordModal(true)}
                className="w-full p-4 mt-3 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 flex items-center justify-between card-shadow transition-colors hover:bg-[#ecd4a3] dark:hover:bg-[#222222]"
              >
                <div className="flex items-center gap-3">
                  <KeyRound className="w-5 h-5 text-[#4478ea]" />
                  <div className="text-left">
                    <h3 className="text-black dark:text-white font-medium">Change Password</h3>
                    <p className="text-sm text-[#666666] dark:text-[#9a9a9a] mt-0.5">Update your account password using your current password or PIN</p>
                  </div>
                </div>
                <span className="text-[#4478ea] text-lg font-light">→</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowVerificationScreen('email')}
                className="w-full p-4 mt-3 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 flex items-center justify-between card-shadow transition-colors hover:bg-[#ecd4a3] dark:hover:bg-[#222222]"
              >
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#4478ea]" />
                  <div className="text-left">
                    <h3 className="text-black dark:text-white font-medium">Change Email</h3>
                    <p className="text-sm text-[#666666] dark:text-[#9a9a9a] mt-0.5">
                      {userProfile?.emailVerified ? 'Email verified ✓ — update your verified email' : 'Verify or update your email address'}
                    </p>
                  </div>
                </div>
                <span className="text-[#4478ea] text-lg font-light">→</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowVerificationScreen('phone')}
                className="w-full p-4 mt-3 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 flex items-center justify-between card-shadow transition-colors hover:bg-[#ecd4a3] dark:hover:bg-[#222222]"
              >
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#4478ea]" />
                  <div className="text-left">
                    <h3 className="text-black dark:text-white font-medium">Change Phone</h3>
                    <p className="text-sm text-[#666666] dark:text-[#9a9a9a] mt-0.5">
                      {userProfile?.phoneVerified ? 'Phone verified ✓ — update your verified number' : 'Verify or update your phone number'}
                    </p>
                  </div>
                </div>
                <span className="text-[#4478ea] text-lg font-light">→</span>
              </motion.button>
            </section>

            <section>
              <h2 className="text-xl text-black dark:text-white mb-4">
                Privacy
              </h2>
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <Eye className="w-5 h-5 text-[#4478ea]" />
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Profile Visibility</h3>
                      <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">Control who can see your profile</p>
                    </div>
                  </div>
                  <Select value={profileVisibility} onValueChange={(value: string) => setProfileVisibility(value as any)}>
                    <SelectTrigger className="w-full bg-[#FAF8F5] dark:bg-black/30 border-black/20 dark:border-white/20 text-black dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/20 dark:border-white/20 modal-shadow">
                      <SelectItem value="public" className="text-black dark:text-white">Public</SelectItem>
                      <SelectItem value="friends" className="text-black dark:text-white">Friends Only</SelectItem>
                      <SelectItem value="private" className="text-black dark:text-white">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                  <div className="flex items-center gap-3 mb-3">
                    <MessageSquare className="w-5 h-5 text-[#4478ea]" />
                    <div className="flex-1">
                      <h3 className="text-black dark:text-white mb-1">Who Can Message You</h3>
                    </div>
                  </div>
                  {/* Persisted to the user document, not just local storage: other
                      viewers read it to decide whether the Message button exists on
                      this profile, and a value that never leaves the device cannot
                      do that. syncFromProfile feeds the Firestore value back into
                      this state when the snapshot arrives. */}
                  <Select
                    value={allowMessages}
                    onValueChange={(value: string) => {
                      setAllowMessages(value as any);
                      updateProfile({ allowMessages: value as 'everyone' | 'friends' | 'none' });
                    }}
                  >
                    <SelectTrigger className="w-full bg-[#FAF8F5] dark:bg-black/30 border-black/20 dark:border-white/20 text-black dark:text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/20 dark:border-white/20 modal-shadow">
                      <SelectItem value="everyone" className="text-black dark:text-white">Everyone</SelectItem>
                      <SelectItem value="friends" className="text-black dark:text-white">Friends Only</SelectItem>
                      <SelectItem value="none" className="text-black dark:text-white">No One</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <MapPin className="w-5 h-5 text-[#4478ea]" />
                      <div>
                        <h3 className="text-black dark:text-white mb-1">Hide Location Globally</h3>
                      </div>
                    </div>
                    <Switch
                      checked={!shareLocation}
                      onCheckedChange={(checked: boolean) => handleShareLocationChange(!checked)}
                    />
                  </div>
                </div>

                {/* Activity status. One global switch, on by default. Reciprocal:
                    the copy says so up front, because losing everyone else's
                    status is the part people would not expect. */}
                <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 transition-colors card-shadow">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                      <Activity className="w-5 h-5 text-[#4478ea] shrink-0" />
                      <div>
                        <h3 className="text-black dark:text-white mb-1">Show my activity status</h3>
                        <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                          Let people see when you were last active. Turn this off and you won't be able to see when others are active either.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={showActivityStatus}
                      onCheckedChange={async (checked: boolean) => {
                        if (!userProfile?.id) return;
                        try {
                          await PresenceService.setMyVisibility(String(userProfile.id), checked);
                        } catch {
                          toast.error('Could not update your activity status. Try again.');
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Blocked Users */}
                <div className="rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 overflow-hidden card-shadow">
                  <button
                    onClick={() => setShowBlockedList(prev => !prev)}
                    className="w-full p-4 flex items-center justify-between hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Ban className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                      <div className="text-left">
                        <h3 className="text-black dark:text-white font-medium">Blocked Users</h3>
                        <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                          {blockedUsers.length === 0 ? 'No blocked users' : `${blockedUsers.length} blocked`}
                        </p>
                      </div>
                    </div>
                    {blockedUsers.length > 0 && (
                      showBlockedList
                        ? <ChevronUp className="w-4 h-4 text-[#666666] dark:text-[#a0a0a0]" />
                        : <ChevronDown className="w-4 h-4 text-[#666666] dark:text-[#a0a0a0]" />
                    )}
                  </button>

                  <AnimatePresence>
                    {showBlockedList && blockedUsers.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-black/10 dark:border-white/10 divide-y divide-black/5 dark:divide-white/5"
                      >
                        {blockedUsers.map(uid => {
                          const profile = blockedUserProfiles[uid];
                          return (
                            <div key={uid} className="flex items-center gap-3 px-4 py-3">
                              <div className="w-10 h-10 rounded-full bg-[#F39C12]/10 dark:bg-[#4478ea]/10 flex items-center justify-center shrink-0 overflow-hidden">
                                {profile?.avatar ? (
                                  <img src={profile.avatar} alt={profile.name} className="w-full h-full object-cover" />
                                ) : (
                                  <UserX className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0 font-medium text-sm text-black dark:text-white">
                                {profile?.name || 'Unknown User'}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => unblockUser(uid)}
                                className="shrink-0 border-[#F39C12] text-[#F39C12] dark:border-[#4478ea] dark:text-[#4478ea]"
                              >
                                Unblock
                              </Button>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </section>

            {/* Administration section */}
            {(userProfile?.rank === 'watcher' || userProfile?.rank === 'legion') && (
              <section>
                <h2 className="text-xl text-black dark:text-white mb-4">Administration</h2>
                <Button
                  onClick={() => window.dispatchEvent(new CustomEvent('openModeratorDashboard'))}
                  className="w-full h-14 bg-[#f2deb3] dark:bg-[#1a1a1a] text-black dark:text-white border border-black/10 dark:border-white/10 justify-between items-center px-4"
                >
                  <div className="flex items-center gap-3">
                    <Gavel className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                    <span>Moderator Dashboard</span>
                  </div>
                  <span>→</span>
                </Button>
              </section>
            )}

            {/* About & Logs */}
            <section className="space-y-6">
              <Button
                variant="outline"
                onClick={async () => {
                  const { AuthService } = await import('../services/api');
                  await AuthService.logout();
                  window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'login' }));
                }}
                className="w-full h-14 border-zinc-500 text-zinc-500 hover:bg-zinc-500 hover:text-white"
              >
                <LogOut className="mr-2 w-5 h-5" /> Log Out
              </Button>

              <div className="pt-4 border-t border-black/10 dark:border-white/10">
                <h2 className="text-xl text-red-500 mb-4 font-bold">Danger Zone</h2>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  className="w-full h-14 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all active:scale-95"
                >
                  <Trash2 className="mr-2 w-5 h-5" /> {isDeleting ? 'Processing...' : 'Delete Account'}
                </Button>
              </div>
            </section>

            {/* Footer */}
            <div className="text-center text-sm text-[#666666] dark:text-[#E5E5E5] pb-8">
              {/* Derived, not hardcoded: a literal year silently goes stale
                  every January and nobody notices until a user reports it. */}
              <p>© {new Date().getFullYear()} AfroYap. All rights reserved.</p>
            </div>
          </div>
        </div>
      </ScrollArea>
      <ReauthDialog
        open={showReauthDialog}
        onOpenChange={setShowReauthDialog}
        onConfirm={handleReauthenticateAndDelete}
        password={reauthPassword}
        setPassword={setReauthPassword}
        loading={isReauthenticating}
      />
      {showSecurityModal && (
        <SecuritySetupModal onClose={() => setShowSecurityModal(false)} />
      )}
      {showChangePasswordModal && (
        <ChangePasswordModal onClose={() => setShowChangePasswordModal(false)} />
      )}
      {showVerificationScreen && createPortal(
        <VerificationScreen
          onClose={() => setShowVerificationScreen(false)}
          initialTab={showVerificationScreen}
        />,
        document.body
      )}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md bg-[#FAF8F5] dark:bg-[#1a1a1a] border-red-500/20">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Final Warning
            </DialogTitle>
            <DialogDescription className="text-zinc-600 dark:text-zinc-400 pt-2 leading-relaxed">
              This action is <span className="font-bold text-red-500">permanent</span>. All your yaps, tribes, friends, and profile data will be purged from AfroYap forever. 
              <br /><br />
              Are you absolutely sure you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 border-black/10 dark:border-white/10 text-black dark:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowDeleteConfirm(false);
                executeDeleteAccount();
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white border-none shadow-lg shadow-red-600/20"
            >
              Confirm Deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReauthDialog({ 
  open, 
  onOpenChange, 
  onConfirm, 
  password, 
  setPassword, 
  loading 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  password: string;
  setPassword: (val: string) => void;
  loading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#FAF8F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl text-black dark:text-white">Confirm Identity</DialogTitle>
          <DialogDescription className="text-zinc-600 dark:text-zinc-400">
            For your security, please enter your password to confirm account deletion.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-white dark:bg-black/30 border-black/10 dark:border-white/10 text-black dark:text-white"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') onConfirm();
            }}
          />
        </div>
        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="flex-1 border-black/10 dark:border-white/10 text-black dark:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white border-none"
          >
            {loading ? 'Confirming...' : 'Confirm & Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
